import { useSignUp, useUser } from "@clerk/clerk-react";
import { AlertCircle, Calendar, Loader2, Users } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEnrollment } from "@/contexts/EnrollmentContext";
import { useUserProfile } from "@/hooks/useUserProfile";
import { api } from "@/services/api";
import type { Programme } from "@/types";

/**
 * One-form free join at /start (KC-504). A signed-out visitor enters their
 * name and email, verifies an emailed code inline, and lands on the dashboard
 * already enrolled in a freemium taster Cohort.
 *
 * The join outlives the Clerk sign-up through sessionStorage: before the new
 * session is activated (which swaps the <SignedOut> route tree for the
 * <SignedIn> one at the same URL) the Cohort id and names are stashed under
 * PENDING_FREE_JOIN_KEY. StartSignedIn reads and clears the key, then saves
 * the names and joins. When the email already has an account the stash is kept
 * across /sign-in, and PendingJoinRedirect (App.tsx) forwards back here.
 */
export const PENDING_FREE_JOIN_KEY = "pending_free_join";

export interface PendingFreeJoin {
  programme_id: string;
  first_name: string;
  last_name: string;
}

export function readPendingFreeJoin(): PendingFreeJoin | null {
  try {
    const raw = sessionStorage.getItem(PENDING_FREE_JOIN_KEY);
    return raw ? (JSON.parse(raw) as PendingFreeJoin) : null;
  } catch {
    return null;
  }
}

function savePendingFreeJoin(pending: PendingFreeJoin) {
  sessionStorage.setItem(PENDING_FREE_JOIN_KEY, JSON.stringify(pending));
}

/** Where to send visitors when no taster has seats. */
function waitlistUrl(): string {
  const base = import.meta.env.VITE_DOMINION_URL || "https://dominionfinancialreview.com/dominion";
  return `${base}#waitlist`;
}

type TasterState =
  | { status: "loading" }
  | { status: "none" }
  | { status: "error" }
  | { status: "ready"; programme: Programme; fellBack: boolean };

const hasSeats = (p: Programme) => p.seats_remaining !== 0;

/** Resolves the requested freemium Cohort, or the soonest-starting one with
 * seats when the id is missing, paid, full or unknown. A Cohort the user is
 * already enrolled in (can_join === false) still resolves, so the caller can
 * send them to the dashboard. */
function useTasterCohort(requestedId: string | null, userId: string | undefined, enabled: boolean) {
  const [state, setState] = useState<TasterState>({ status: "loading" });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const all = await api.getAvailableProgrammes(userId);
        const linked = requestedId
          ? (all.find((p) => p.id === requestedId) ??
            (await api.getProgrammeById(requestedId, userId)))
          : null;
        let next: TasterState;
        if (linked?.is_freemium && (hasSeats(linked) || linked.can_join === false)) {
          next = { status: "ready", programme: linked, fellBack: false };
        } else {
          const soonest = all
            .filter((p) => p.is_freemium && hasSeats(p))
            .sort((a, b) => Date.parse(a.start_date) - Date.parse(b.start_date))[0];
          next = soonest
            ? { status: "ready", programme: soonest, fellBack: requestedId != null }
            : { status: "none" };
        }
        if (!cancelled) setState(next);
      } catch {
        if (!cancelled) setState({ status: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requestedId, userId, enabled]);

  return state;
}

const FIELD_CLASS =
  "w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-white/40";
const LABEL_CLASS = "block text-white/70 text-sm mb-1 text-left";
const BUTTON_CLASS =
  "w-full bg-white text-sidebar px-6 py-4 rounded-xl font-bold hover:bg-white/90 disabled:opacity-70 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2";

function StartShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
      <div className="bg-white/10 backdrop-blur-sm p-8 sm:p-12 rounded-2xl shadow-xl max-w-md w-full border border-white/10">
        <div className="flex flex-col items-center text-center mb-6">
          <img src="/dfr-logo.svg" alt="DFR Logo" className="h-12 w-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-1">Try Dominion free</h1>
          <p className="text-white/60">A one-week taster of the Dominion Programme.</p>
        </div>
        {children}
      </div>
    </div>
  );
}

function NoTasterOpen() {
  return (
    <div className="text-center">
      <p className="text-white font-semibold mb-2">No free taster open right now</p>
      <p className="text-white/60 text-sm mb-6">
        Join the waitlist and we'll tell you when the next one opens.
      </p>
      <a href={waitlistUrl()} className={BUTTON_CLASS}>
        Join the waitlist
      </a>
    </div>
  );
}

function TasterSummary({ programme, fellBack }: { programme: Programme; fellBack: boolean }) {
  const weeks = programme.total_weeks;
  const places = programme.seats_remaining;
  return (
    <div className="mb-6">
      {fellBack && (
        <p className="text-amber-300 text-sm flex items-center gap-2 mb-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          That taster isn't open — here's the next free taster with places.
        </p>
      )}
      <div className="rounded-xl border border-white/20 bg-white/5 p-4 text-left">
        <p className="text-white font-semibold text-lg">{programme.name}</p>
        <div className="mt-2 space-y-1 text-white/70 text-sm">
          <p className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Starts{" "}
            {new Date(programme.start_date).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </p>
          {/* A full-programme week holds two Work Packages (4 weeks, 8 Reports). */}
          <p>
            {weeks} {weeks === 1 ? "week" : "weeks"} · {weeks * 2} Work Packages
          </p>
          {places != null && (
            <p className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              {places === 1 ? "1 place left" : `${places} places left`}
            </p>
          )}
        </div>
        <p className="mt-3 text-emerald-300 font-semibold text-sm">Free — no card needed</p>
      </div>
    </div>
  );
}

function clerkErrors(err: unknown): { code?: string; message?: string; longMessage?: string }[] {
  const errors = (err as { errors?: unknown })?.errors;
  return Array.isArray(errors) ? errors : [];
}

function clerkMessage(err: unknown, fallback: string): string {
  const first = clerkErrors(err)[0];
  if (first) return first.longMessage || first.message || fallback;
  return err instanceof Error ? err.message : fallback;
}

/** /start for visitors without an account. */
export function StartSignedOut() {
  const [searchParams] = useSearchParams();
  const state = useTasterCohort(searchParams.get("programme_id"), undefined, true);
  const { signUp, setActive } = useSignUp();
  const [step, setStep] = useState<"details" | "code" | "exists">("details");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (state.status === "loading") {
    return (
      <StartShell>
        <Loader2 className="w-6 h-6 animate-spin text-white mx-auto" />
      </StartShell>
    );
  }
  if (state.status !== "ready") {
    return (
      <StartShell>
        {state.status === "error" ? (
          <p className="text-red-400 text-sm text-center">
            Couldn't load the free taster. Please try again later.
          </p>
        ) : (
          <NoTasterOpen />
        )}
      </StartShell>
    );
  }

  const { programme } = state;
  const pending = (): PendingFreeJoin => ({
    programme_id: programme.id,
    first_name: firstName.trim(),
    last_name: lastName.trim(),
  });

  const handleDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp || !setActive) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await signUp.create({
        emailAddress: email.trim(),
        // Clerk requires a password on this instance; without it the sign-up
        // stays `missing_requirements` after the email code.
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      if (result.status === "complete" && result.createdSessionId) {
        savePendingFreeJoin(pending());
        await setActive({ session: result.createdSessionId });
        return;
      }
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      setStep("code");
    } catch (err) {
      if (clerkErrors(err).some((e) => e.code === "form_identifier_exists")) {
        // Keep the join so it completes after they sign in instead.
        savePendingFreeJoin(pending());
        setStep("exists");
        return;
      }
      setError(clerkMessage(err, "Couldn't start your sign-up. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp || !setActive) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await signUp.attemptEmailAddressVerification({ code: code.trim() });
      if (result.status === "complete" && result.createdSessionId) {
        // Stash before activating: activation swaps to the signed-in route
        // tree, where StartSignedIn finishes the join.
        savePendingFreeJoin(pending());
        await setActive({ session: result.createdSessionId });
        return;
      }
      setError("We couldn't finish your sign-up. Please try again.");
    } catch (err) {
      setError(clerkMessage(err, "Verification failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StartShell>
      <TasterSummary programme={programme} fellBack={state.fellBack} />

      {step === "details" && (
        <form onSubmit={handleDetails} className="space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="start-first-name" className={LABEL_CLASS}>
                First name
              </label>
              <input
                id="start-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoComplete="given-name"
                className={FIELD_CLASS}
              />
            </div>
            <div className="flex-1">
              <label htmlFor="start-last-name" className={LABEL_CLASS}>
                Last name
              </label>
              <input
                id="start-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                autoComplete="family-name"
                className={FIELD_CLASS}
              />
            </div>
          </div>
          <div>
            <label htmlFor="start-email" className={LABEL_CLASS}>
              Email
            </label>
            <input
              id="start-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className={FIELD_CLASS}
            />
          </div>
          <div>
            <label htmlFor="start-password" className={LABEL_CLASS}>
              Password
            </label>
            <input
              id="start-password"
              type="password"
              placeholder="Choose a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className={FIELD_CLASS}
            />
          </div>
          {error && <p className="text-red-400 text-sm text-left">{error}</p>}
          <button
            type="submit"
            disabled={
              submitting ||
              !firstName.trim() ||
              !lastName.trim() ||
              !email.trim() ||
              password.length < 8
            }
            className={BUTTON_CLASS}
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Start free
          </button>
        </form>
      )}

      {step === "code" && (
        <form onSubmit={handleCode} className="space-y-3">
          <p className="text-white/60 text-sm">
            We've emailed a code to <span className="text-white">{email.trim()}</span>.
          </p>
          <label htmlFor="start-code" className={LABEL_CLASS}>
            Verification code
          </label>
          <input
            id="start-code"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
            autoFocus
            autoComplete="one-time-code"
            className={`${FIELD_CLASS} tracking-widest text-center`}
          />
          {error && <p className="text-red-400 text-sm text-left">{error}</p>}
          <button type="submit" disabled={submitting || !code.trim()} className={BUTTON_CLASS}>
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Verify and start
          </button>
        </form>
      )}

      {step === "exists" && (
        <div className="text-center">
          <p className="text-white font-semibold mb-2">You already have an account</p>
          <p className="text-white/60 text-sm mb-6">
            <span className="text-white">{email.trim()}</span> is already registered. Sign in and
            we'll add you to {programme.name}.
          </p>
          <Link to="/sign-in" className={BUTTON_CLASS}>
            Sign in
          </Link>
        </div>
      )}
    </StartShell>
  );
}

/** /start for signed-in users: finish a pending join, or join directly. */
export function StartSignedIn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile, isLoading, refetch: refetchProfile } = useUserProfile();
  const { refetch: refetchEnrollment } = useEnrollment();
  const { user: clerkUser } = useUser();
  const [pending] = useState(readPendingFreeJoin);
  // Consumed once read, so a failed join can't bounce every later page back
  // here through PendingJoinRedirect.
  useEffect(() => sessionStorage.removeItem(PENDING_FREE_JOIN_KEY), []);
  const state = useTasterCohort(
    pending?.programme_id ?? searchParams.get("programme_id"),
    userProfile?.id,
    !isLoading && userProfile != null
  );
  const started = useRef(false);
  const [askNames, setAskNames] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");

  const join = useCallback(
    async (programme: Programme, names?: { first_name: string; last_name: string }) => {
      if (!userProfile) return;
      setJoining(true);
      setError("");
      try {
        if (names) {
          // Same write order as ProgramRegistrationPage: Clerk, then our profile.
          await clerkUser?.update({ firstName: names.first_name, lastName: names.last_name });
          await api.updateMyIdentity(names);
          await refetchProfile();
        }
        await api.joinProgramme(programme.id, userProfile.id);
        await refetchEnrollment();
        navigate("/", { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't join the taster.");
        setJoining(false);
      }
    },
    [userProfile, clerkUser, refetchProfile, refetchEnrollment, navigate]
  );

  useEffect(() => {
    if (state.status !== "ready" || !userProfile || started.current) return;
    started.current = true;
    const { programme } = state;
    if (programme.can_join === false) {
      navigate("/", { replace: true });
      return;
    }
    if (userProfile.first_name?.trim() && userProfile.last_name?.trim()) {
      join(programme);
      return;
    }
    if (pending?.first_name && pending.last_name) {
      join(programme, { first_name: pending.first_name, last_name: pending.last_name });
      return;
    }
    setAskNames(true);
  }, [state, userProfile, pending, join, navigate]);

  if (state.status === "none") {
    return (
      <StartShell>
        <NoTasterOpen />
      </StartShell>
    );
  }
  if (state.status === "error") {
    return (
      <StartShell>
        <p className="text-red-400 text-sm text-center">
          Couldn't load the free taster. Please try again later.
        </p>
      </StartShell>
    );
  }
  if (state.status !== "ready" || !askNames) {
    return (
      <StartShell>
        {error ? (
          <p className="text-red-400 text-sm text-center">{error}</p>
        ) : (
          <Loader2 className="w-6 h-6 animate-spin text-white mx-auto" />
        )}
      </StartShell>
    );
  }

  const { programme } = state;
  return (
    <StartShell>
      <TasterSummary programme={programme} fellBack={state.fellBack} />
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          join(programme, { first_name: firstName.trim(), last_name: lastName.trim() });
        }}
      >
        <p className="text-white/60 text-sm text-left">
          Add your name to start. It appears on your participation badge.
        </p>
        <div className="flex gap-3">
          <div className="flex-1">
            <label htmlFor="start-first-name" className={LABEL_CLASS}>
              First name
            </label>
            <input
              id="start-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              autoComplete="given-name"
              className={FIELD_CLASS}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="start-last-name" className={LABEL_CLASS}>
              Last name
            </label>
            <input
              id="start-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              autoComplete="family-name"
              className={FIELD_CLASS}
            />
          </div>
        </div>
        {error && <p className="text-red-400 text-sm text-left">{error}</p>}
        <button
          type="submit"
          disabled={joining || !firstName.trim() || !lastName.trim()}
          className={BUTTON_CLASS}
        >
          {joining && <Loader2 className="w-4 h-4 animate-spin" />}
          Start free
        </button>
      </form>
    </StartShell>
  );
}
